import { appIntl } from '@nxsys/helpers/Common';
import { IAepn } from '@nxsys/types/models/aepn';
import { IContribution } from '@nxsys/types/models/aepn/contribution';
import { IAepnUpload } from '@nxsys/types/models/aepn/upload';
import { IPayroll } from '@nxsys/types/models/payroll';
import { PaginationType } from '@nxsys/types/models/Common';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { aepnActs } from '../action';
import { GetAEPNListParams } from '../types';

export interface AEPNState {
  aepnList: {
    loading: boolean;
    list: IAepn[];
    pagination: PaginationType;
    queryParams: GetAEPNListParams | null;
  };
  uploadHistory: {
    loading: boolean;
    list: IAepnUpload[];
    totalDocs: number;
  };
  contributionsHistory: {
    loading: boolean;
    list: IContribution[];
    totalDocs: number;
  };
  payrollsPending: {
    loading: boolean;
    list: IPayroll[];
    totalDocs: number;
  };
  // Generic loading for actions that don't store data (upload, download, submit, etc.)
  actionLoading: boolean;
}

const initialState: AEPNState = {
  aepnList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  uploadHistory: {
    loading: false,
    list: [],
    totalDocs: 0,
  },
  contributionsHistory: {
    loading: false,
    list: [],
    totalDocs: 0,
  },
  payrollsPending: {
    loading: false,
    list: [],
    totalDocs: 0,
  },
  actionLoading: false,
};

const aepnSlice = createSlice({
  name: 'aepn',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // --- AEPN List ---
    builder.addCase(aepnActs.getAEPNList.pending, (state) => {
      state.aepnList.loading = true;
    });
    builder.addCase(aepnActs.getAEPNList.fulfilled, (state, action) => {
      state.aepnList.loading = false;
      state.aepnList.list = action.payload.data;
      state.aepnList.pagination = action.payload.pagination;
      state.aepnList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(aepnActs.getAEPNList.rejected, (state, action) => {
      state.aepnList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'aepn.getError' }),
      });
    });

    // --- Upload ---
    builder.addCase(aepnActs.aepnUpload.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.aepnUpload.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.aepnUpload.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.aepnUploadSave.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.aepnUploadSave.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.aepnUploadSave.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.getUploadHistory.pending, (state) => {
      state.uploadHistory.loading = true;
    });
    builder.addCase(aepnActs.getUploadHistory.fulfilled, (state, { payload }) => {
      state.uploadHistory.loading = false;
      state.uploadHistory.list = payload.data || payload;
      state.uploadHistory.totalDocs = payload.totalDocs || 0;
    });
    builder.addCase(aepnActs.getUploadHistory.rejected, (state) => {
      state.uploadHistory.loading = false;
    });

    builder.addCase(aepnActs.getUploadHistoryById.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.getUploadHistoryById.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.getUploadHistoryById.rejected, (state) => {
      state.actionLoading = false;
    });

    // --- Download ---
    builder.addCase(aepnActs.aepnDownload.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.aepnDownload.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.aepnDownload.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.aepnDownloadForEmployee.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.aepnDownloadForEmployee.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.aepnDownloadForEmployee.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.aepnDownloadForCompany.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.aepnDownloadForCompany.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.aepnDownloadForCompany.rejected, (state) => {
      state.actionLoading = false;
    });

    // --- Payroll Pending ---
    builder.addCase(aepnActs.getPayrollPending.pending, (state) => {
      state.payrollsPending.loading = true;
    });
    builder.addCase(aepnActs.getPayrollPending.fulfilled, (state, { payload }) => {
      state.payrollsPending.loading = false;
      state.payrollsPending.list = payload.data || payload;
      state.payrollsPending.totalDocs = payload.totalDocs || 0;
    });
    builder.addCase(aepnActs.getPayrollPending.rejected, (state) => {
      state.payrollsPending.loading = false;
    });

    // --- Contributions History ---
    builder.addCase(aepnActs.getContributionsHistory.pending, (state) => {
      state.contributionsHistory.loading = true;
    });
    builder.addCase(aepnActs.getContributionsHistory.fulfilled, (state, { payload }) => {
      state.contributionsHistory.loading = false;
      state.contributionsHistory.list = payload.data || payload;
      state.contributionsHistory.totalDocs = payload.totalDocs || 0;
    });
    builder.addCase(aepnActs.getContributionsHistory.rejected, (state) => {
      state.contributionsHistory.loading = false;
    });

    // --- Submit/Resubmit/Status/Errors/Export (action-only, no state storage) ---
    builder.addCase(aepnActs.submitContributions.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.submitContributions.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.submitContributions.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.resubmitContributions.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.resubmitContributions.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.resubmitContributions.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.getContributionsStatus.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.getContributionsStatus.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.getContributionsStatus.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.getContributionsErrors.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.getContributionsErrors.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.getContributionsErrors.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(aepnActs.exportContributions.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(aepnActs.exportContributions.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(aepnActs.exportContributions.rejected, (state) => {
      state.actionLoading = false;
    });
  },
});

export const aepnReducer = aepnSlice.reducer;
export const aepnActions = {
  ...aepnSlice.actions,
  ...aepnActs,
};
